import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  /** One sentence on why it's empty or what will appear here. */
  description?: ReactNode;
  /** Optional next step, e.g. a secondary "Create" button. */
  action?: ReactNode;
  /** Sizing / spacing for the surrounding container (defaults to `py-12`). */
  className?: string;
}

/**
 * The dashboard's one empty-state pattern: an icon tile, a headline, one line
 * of explanation and an optional action, centred in whatever holds it — a
 * table body, a card, or a page section. Neutral greys only, so it reads the
 * same under every tenant brand colour.
 */
export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-4 py-12 text-center', className)}>
      <span className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-white text-muted-foreground">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <p className="relative mt-3 text-sm font-medium text-foreground">{title}</p>
      {description && <p className="relative mt-1 max-w-sm text-xs text-muted-foreground">{description}</p>}
      {action && <div className="relative mt-4">{action}</div>}
    </div>
  );
}
